import {Button, Group, Modal, Stack, Text} from '@mantine/core'
import {useState} from 'react'

import {type ContactFieldErrors, ContactFormFields, validateContact} from './contact-form-fields'
import {type ContactWriteInput, createEmptyContact, type CustomerContact} from './schemas'
import {useCreateCustomerContact, useUpdateCustomerContact} from './use-customers'

export type InlineCreateCustomerContactDialogProps = {
    customerId: string
    opened: boolean
    onClose: () => void
    /** Called with the newly created contact so callers can auto-select it. */
    onCreated?: (contact: CustomerContact) => void
    contact?: CustomerContact
}

function contactToDraft(contact: CustomerContact): ContactWriteInput {
    return {
        name: contact.name,
        title: contact.title ?? '',
        description: contact.description ?? '',
        phone: contact.phone ?? '',
        email: contact.email ?? '',
    }
}

/**
 * Inline dialog for creating or editing a customer contact. Posts through the customer
 * React Query feature so cache invalidation is shared with other surfaces.
 */
export function InlineCreateCustomerContactDialog({
    customerId,
    opened,
    onClose,
    onCreated,
    contact,
}: InlineCreateCustomerContactDialogProps) {
    const createContact = useCreateCustomerContact(customerId)
    const updateContact = useUpdateCustomerContact(customerId)
    const [draft, setDraft] = useState<ContactWriteInput>(
        contact ? contactToDraft(contact) : createEmptyContact(),
    )
    const [fieldErrors, setFieldErrors] = useState<ContactFieldErrors | null>(null)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)

    const saving = createContact.isPending || updateContact.isPending

    const openedFor = opened ? (contact ?? 'new') : null
    const [draftFor, setDraftFor] = useState(openedFor)
    if (openedFor !== draftFor) {
        setDraftFor(openedFor)
        if (openedFor != null) {
            setDraft(contact ? contactToDraft(contact) : createEmptyContact())
        }
    }

    function reset() {
        setDraft(createEmptyContact())
        setFieldErrors(null)
        setErrorMessage(null)
    }

    function handleClose() {
        reset()
        onClose()
    }

    function updateDraft(partial: Partial<ContactWriteInput>) {
        setDraft((current) => ({...current, ...partial}))
    }

    async function handleSave() {
        const validation = validateContact(draft)
        if (validation.nameError || validation.emailError) {
            setFieldErrors(validation)
            setErrorMessage('Fix contact errors before saving')
            return
        }

        setFieldErrors(null)
        setErrorMessage(null)
        try {
            if (contact) {
                await updateContact.mutateAsync({contactId: contact.id, input: draft})
            } else {
                const created = await createContact.mutateAsync(draft)
                onCreated?.(created)
            }
            reset()
            onClose()
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to save contact')
        }
    }

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title={contact ? 'Edit customer contact' : 'Add customer contact'}
            size="md"
            centered
        >
            <Stack gap="md">
                {errorMessage ? (
                    <Text size="sm" c="red">
                        {errorMessage}
                    </Text>
                ) : null}

                <ContactFormFields
                    draft={draft}
                    errors={fieldErrors ?? undefined}
                    onUpdate={updateDraft}
                />

                <Group justify="flex-end" gap="sm">
                    <Button
                        variant="default"
                        size="compact-sm"
                        onClick={handleClose}
                        disabled={saving}
                    >
                        Cancel
                    </Button>
                    <Button size="compact-sm" onClick={() => void handleSave()} loading={saving}>
                        Save
                    </Button>
                </Group>
            </Stack>
        </Modal>
    )
}
